/**
 * Light/dark/system theme switch.
 *
 * Three explicit choices — `light`, `dark`, `system`. `system` keeps the
 * app in sync with the OS preference live (no need to revisit the picker
 * after a system theme change). Persisted to localStorage and reflected
 * onto <html class="dark"> for Tailwind v4's `@custom-variant dark`.
 *
 * Splash bootstrap (`src/app/splash.ts`) reads the same storage key to
 * paint the first frame in the right mode (no light→dark flash).
 */
import { ref, watch } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'ezqr:theme'

const theme = ref<ThemeMode>('system')

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
  )
}

function apply(mode: ThemeMode) {
  if (typeof document === 'undefined') return
  const dark = mode === 'dark' || (mode === 'system' && prefersDark())
  document.documentElement.classList.toggle('dark', dark)
}

function isMode(v: unknown): v is ThemeMode {
  return v === 'light' || v === 'dark' || v === 'system'
}

/**
 * Singleton init guard. The matchMedia listener and the initial
 * theme load only need to run once for the whole app — every
 * subsequent `useTheme()` call should reuse the existing shared
 * state (the `theme` ref is module-scope, so it does). Without this
 * each `useTheme()` invocation used to re-add a matchMedia listener
 * that was never removed; in a long-lived session every UserMenu
 * mount-cycle accumulated one more dangling listener.
 */
let booted = false

function bootstrapOnce(): void {
  if (booted) return
  booted = true

  let stored: string | null = null
  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch {
    /* private-mode Safari etc. */
  }
  theme.value = isMode(stored) ? stored : 'system'
  apply(theme.value)

  // Persist + reapply on every change. Module-scope watcher so it
  // also fires for `set()` calls from any future caller.
  watch(theme, (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      /* ignore */
    }
    apply(mode)
  })

  // When 'system' is selected, react to OS-level theme changes live.
  // No cleanup needed — the listener is bound to the page lifetime
  // and the singleton flag prevents re-attaching it.
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', () => {
      if (theme.value === 'system') apply('system')
    })
  }
}

export function useTheme() {
  // Lazy bootstrap on first consumer. Works in both client and SSR —
  // when running outside a browser, `window` is `undefined` and
  // bootstrap simply no-ops on the matchMedia path.
  bootstrapOnce()

  function set(mode: ThemeMode) {
    theme.value = mode
  }

  return { theme, set }
}
